import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { effect, inject } from '@angular/core';
import { LocationStrategy } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';

import { filter, map } from 'rxjs';

import { patchState, StateSignals, WritableStateSource } from '@ngrx/signals';

import { navigationDataAdapterMethodsFactory } from './navigation.data-adapter-methods';
import { NavigationPositionItem } from '../models/navigation-position-item.type';
import { NavigationState } from './navigation.store';

export const navigationHooksFactory = (
  store: WritableStateSource<NavigationState> & StateSignals<NavigationState> & ReturnType<typeof navigationDataAdapterMethodsFactory>,
  breakpointObserver = inject(BreakpointObserver),
  locationStrategy = inject(LocationStrategy),
  router = inject(Router),
  title = inject(Title),
) => {
  const currentUrl = toSignal(
    router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.url),
    ),
  );
  const handset = toSignal(breakpointObserver.observe(Breakpoints.Handset).pipe(map((state) => state.matches)), { requireSync: true });

  const hideOnHandsetAfterNavigationEnds = () => handset() && currentUrl() && patchState(store, { hiddenOnHandset: true });

  const loadPositions = () =>
    store.products().forEach((product) => {
      const { application, itemsUrl } = product;
      itemsUrl && store.loadPositions({ application, itemsUrl });
    });

  const loadProducts = () => store.loadProducts(handset());

  const updatePageTitle = () => {
    const url = currentUrl();

    if (url) {
      const findActivePosition = (positions: NavigationPositionItem[]): NavigationPositionItem | null => {
        for (const position of positions) {
          if (position.items) {
            findActivePosition(position.items);
          } else if (position.url && url.includes(position.url.replace(locationStrategy.getBaseHref(), ''))) {
            return position;
          }
        }

        return null;
      };

      for (const product of store.products()) {
        const positions = store.positionsMap().get(product.application) || [];
        const activePosition = findActivePosition(positions);

        if (activePosition) {
          title.setTitle(`${activePosition.label} - ${product.fullName}`);
          break;
        }
      }
    }
  };

  return {
    onInit() {
      effect(hideOnHandsetAfterNavigationEnds);
      effect(loadPositions);
      effect(loadProducts);
      effect(updatePageTitle);
    },
  };
};
